import {
	Avatar,
	Badge,
	Box,
	Button,
	Chip,
	Stack,
	Typography,
	styled,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { SHORE_BADGE_ICON,SHIP_BADGE_ICON } from "../../../utils/constants";
const StyledBadge = styled(Badge)({
	"& .MuiBadge-badge": {
		right: "4px", 
		bottom: "20px",
		transform: "scale(1) translate(50%, 50%)",
		width: "4rem",
		height: "4rem",
	},
});

const WelcomeButton = styled(Button)({
	backgroundColor: "#376AFA",
	fontSize: "16px",
	textTransform: "unset",
	width: "40%",
	cursor: "pointer",
	height: "56px",
	"&:hover": {
		backgroundColor: "#114ef5",
	},
});

const DeclineButton = styled(Button)({
	backgroundColor: "#fff",
	fontSize: "16px",
	textTransform: "unset",
	border: "1px solid #376AFA",
	width: "40%",
	cursor: "pointer",
	height: "56px",
});

const ContactDetailsContainer = styled(Box)({
	minHeight: "100%",
	width: "50%",
});

const StyledUsernameHeading = styled(Typography)({
	fontSize: "22px",
	fontWeight: "700",
	textAlign: "center",
	fontFamily: "Figtree-Regular",
});

const StyledGeneralTypography = styled(Typography)({
	fontSize: "16px",
	fontWeight: "600",
	textAlign: "center",
	fontFamily: "Figtree-Regular",
});

const getBadgeIcon = (contactData) => {
	if (contactData?.contactType === "local") return null;
	let { rankLevel1 } = contactData;
	let rankLevel1SmallCase = (rankLevel1 || "").toLowerCase();

	let iconPath;
	switch (rankLevel1SmallCase) {
		case "shore":
			iconPath = SHIP_BADGE_ICON;
			break;
		case "ship":
			iconPath = SHORE_BADGE_ICON;
			break;
		default:
			iconPath = "";
	}
	return (
		iconPath && (
			<img
				src={iconPath}
				alt={`${rankLevel1SmallCase} badge`}
				style={{ width: "100%" }}
			/>
		)
	);
};

const NoContactSelected = () => {
	return (
		<ContactDetailsContainer>
			<Stack
				flexDirection="column"
				alignItems="center"
				gap={1}
				sx={{ height: "100%" }}
				justifyContent="center"
			>
				<img loading="lazy" src="/img/no-contacts-illustration.svg" height="75%" />
				<Typography
					variant="body1"
					sx={{
						fontWeight: "700",
						color: "#15194B",
						fontFamily: "Figtree-Regular",
					}}
				>
					No contact is selected
				</Typography>
			</Stack>
		</ContactDetailsContainer>
	);
};

function SelectedContactDetails(props) {
	const { selectedPendingContact, ignored: ignoredContacts } = useSelector(
		(state) => state.contacts
	);

	const setProfileImg = (userId) => {
		if (selectedPendingContact.contactType === "local") {
			if (props.allProfileImages) {
				return props.allProfileImages[userId + selectedPendingContact.userId];
			}
		} else {
			if (props.allProfileImages) {
				return props.allProfileImages[selectedPendingContact.userId];
			}
		}
	};

	const getAvatarText = () => {
		let letters = selectedPendingContact.userName
			? selectedPendingContact.userName
					.split(" ")
					.map((n) => n[0])
					.join("")
			: "";
		return letters.slice(0, 2).toUpperCase();
	};

	const isContactIgnored = () => {
		let contactStatus = ignoredContacts.filter(
			(item) => item?.userId === selectedPendingContact?.userId
		);
		return contactStatus.length === 0;
	};

	if (Object.keys(selectedPendingContact).length === 0)
		return <NoContactSelected />;

	let subTitle = selectedPendingContact.waitingForConfirmation
		? "Awaiting Confirmation"
		: "Wishes to join your maritime journey";
	let description = selectedPendingContact.waitingForConfirmation
		? "You can view their contact details after they have accepted your invitation"
		: "Would you like to add them to your onship network?";

	let imgSrc =
		selectedPendingContact &&
		setProfileImg(selectedPendingContact?.userId);

	let rank = selectedPendingContact.rankLevel2 || selectedPendingContact.rank;
	let role = selectedPendingContact.rankLevel3 || selectedPendingContact.role;

	return (
		<ContactDetailsContainer>
			<Stack
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				gap={2}
			>
				<StyledBadge
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					badgeContent={getBadgeIcon(selectedPendingContact)}
				>
					{imgSrc ? (
						<Avatar
							src={imgSrc}
							sx={{
								width: 106,
								height: 107,
								borderRadius: "50%",
							}}
						/>
					) : (
						<Avatar
							sx={{
								bgcolor: "#638dff",
								width: 106,
								height: 107,
								fontSize: "3.5rem",
								fontWeight: "300",
							}}
						>
							{getAvatarText()}
						</Avatar>
					)}
				</StyledBadge>
				<Stack flexDirection='row' alignItems='center' gap={1}>
					{rank?.trim() && (
						<Chip
							label={rank?.trim()}
							color="success"
						/>
					)}
					{role?.trim() && (
						<Typography sx={{ fontSize: '14px', fontFamily: 'Figtree-Regular' }}>{role}</Typography>
					)}
				</Stack>
				<StyledUsernameHeading>
					{selectedPendingContact.userName}
				</StyledUsernameHeading>
				<Stack
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					sx={{ mt: 3 }}
					gap={1}
				>
					<StyledUsernameHeading sx={{ fontSize: "16px", color: "#15194B" }}>
						{subTitle}
					</StyledUsernameHeading>
					<StyledGeneralTypography
						sx={{ fontSize: "16px", color: "#727484", width: "90%" }}
					>
						{description}
					</StyledGeneralTypography>
				</Stack>
				{!selectedPendingContact?.waitingForConfirmation && (
					<Stack
						flexDirection="row"
						gap={2}
						alignItems="center"
						justifyContent="center"
						sx={{ width: "100%", mt: 2 }}
					>
						{isContactIgnored() && (
							<DeclineButton disabled={props.showOverlayLoader}
								variant="outlined"
								onClick={() => props.ignore(selectedPendingContact)}
							>
								Ignore
							</DeclineButton>
						)}
						<WelcomeButton disabled={props.showOverlayLoader}
							variant="contained"
							onClick={() => props.accept(selectedPendingContact)}
						>
							Accept
						</WelcomeButton>
					</Stack>
				)}
			</Stack>
		</ContactDetailsContainer>
	);
}

export default SelectedContactDetails;
